/**
 * Jeu de connexions SIMULEES pour previsualiser la vue cards de la section
 * synchro sans connexion Tradovate reelle.
 *
 * POURQUOI CE MODULE EXISTE. La preprod ne porte AUCUNE connexion Tradovate et
 * la base de dev locale est inexploitable : le rendu des cards ne pouvait donc
 * etre constate nulle part avant la prod. Ce module fabrique les cas qui
 * different visuellement, pour qu'un ecran se relise sans attendre un vrai
 * compte : plusieurs comptes, un seul, AUCUN, et un parc DEBORDANT qui exerce
 * le compteur « +N comptes ». Les deux environnements y figurent, le badge ne
 * se jugeant qu'en le voyant dans ses deux etats.
 *
 * ⚠️ CE N'EST PAS UN MODE DE SECOURS. Il ne doit jamais pouvoir s'activer en
 * production : le garde vit dans `isSyncDemoEnabled`, qui exige A LA FOIS le
 * parametre d'URL ET un build de developpement. Un build de prod ne peut donc
 * pas afficher ces cartes, meme avec l'URL exacte.
 *
 * MODULE PUR : aucun import `wasp/*` ni React, meme convention que
 * `tradovateSyncPresentation.ts`.
 */

/** Forme minimale consommee par la vue cards, alignee sur la vraie requete. */
export interface DemoSyncedAccount {
  id: string;
  /** Identifiant broker, affiche tel quel dans le noeud de droite. */
  name: string;
}

export interface DemoConnection {
  id: string;
  /** Rattachement a une prop firm ; `null` = connexion non assignee. */
  propFirmTemplate: {
    id: string;
    name: string;
    slug: string;
    logoUrl: string | null;
    color: string;
  } | null;
  /**
   * Hote API de la connexion. Les deux valeurs doivent etre representees dans
   * le jeu de demonstration, le badge n'etant jugeable qu'en le voyant dans
   * ses deux etats.
   */
  environment: "live" | "demo";
  /** Cf. `tradovateDisplayStatus.ts`. */
  displayStatus: string;
  /** Cf. `tradovateRealtimeStatus.ts`. */
  realtimeStatus: string;
  lastSyncAt: Date | null;
  activeSyncedAccountCount: number;
  demoAccounts: DemoSyncedAccount[];
}

/**
 * ⚠️ Les `slug` doivent exister dans `LOCAL_PROPFIRM_LOGOS`, sans quoi la
 * demonstration montrerait la pastille de repli et non le logo, c'est-a-dire
 * pas le rendu qu'on cherche a juger. Verifies contre
 * `app/public/logos/propfirms/`.
 */
export const DEMO_CONNECTIONS: DemoConnection[] = [
  // Cas 1 — en bonne sante, plusieurs comptes, flux en direct.
  {
    id: "demo-conn-apex",
    environment: "demo",
    propFirmTemplate: {
      id: "demo-firm-apex",
      name: "Apex Trader Funding",
      slug: "apex",
      logoUrl: null,
      color: "#F59E0B",
    },
    displayStatus: "active",
    realtimeStatus: "live",
    lastSyncAt: new Date(),
    activeSyncedAccountCount: 2,
    demoAccounts: [
      { id: "demo-acc-1", name: "APEX5455610000009" },
      { id: "demo-acc-2", name: "APEX5455610000017" },
    ],
  },
  // Cas 2 — reconnexion requise : le badge passe en ambre et la barre de pied
  // ne propose plus que « Reconnecter ».
  {
    id: "demo-conn-topstep",
    environment: "live",
    propFirmTemplate: {
      id: "demo-firm-topstep",
      name: "Topstep",
      slug: "topstep",
      logoUrl: null,
      color: "#2563EB",
    },
    displayStatus: "needs_reauth",
    realtimeStatus: "interrupted",
    lastSyncAt: new Date(Date.now() - 1000 * 60 * 60 * 26),
    activeSyncedAccountCount: 1,
    demoAccounts: [{ id: "demo-acc-3", name: "FTDFYG50732510883" }],
  },
  // Cas 3 — connexion vivante, ZERO compte synchronise. Cas reel (Lucid
  // Trading) : l'animation doit rester complete, cote droit vide et explique.
  {
    id: "demo-conn-lucid",
    environment: "demo",
    propFirmTemplate: {
      id: "demo-firm-lucid",
      name: "Lucid Trading",
      slug: "lucid",
      logoUrl: null,
      color: "#22C55E",
    },
    displayStatus: "active_no_accounts",
    realtimeStatus: "not_enabled",
    lastSyncAt: new Date(Date.now() - 1000 * 60 * 12),
    activeSyncedAccountCount: 0,
    demoAccounts: [],
  },
  // Cas 4 — PARC DEBORDANT. C'est le cas qui a motive le passage de 4 a 5
  // comptes visibles : avec 15 comptes, l'ancienne card en montrait 4 et
  // ROGNAIT la pastille « +N », seule trace de l'existence des 11 autres. La
  // demonstration doit donc porter franchement plus de comptes que le plafond,
  // sans quoi le compteur ne se verifie pas.
  {
    id: "demo-conn-takeprofit",
    environment: "live",
    propFirmTemplate: {
      id: "demo-firm-takeprofit",
      name: "Take Profit Trader",
      slug: "takeprofittrader",
      logoUrl: null,
      color: "#8B5CF6",
    },
    displayStatus: "active",
    realtimeStatus: "live",
    lastSyncAt: new Date(Date.now() - 1000 * 60 * 3),
    activeSyncedAccountCount: 15,
    demoAccounts: Array.from({ length: 15 }, (_, i) => ({
      id: `demo-acc-overflow-${i + 1}`,
      name: `TPT${String(60104220000 + i * 7).padStart(14, "0")}`,
    })),
  },
];

/**
 * Le mode demo est-il actif ?
 *
 * DEUX CONDITIONS CUMULATIVES, et la seconde n'est pas une precaution de style :
 * `isDev` est fige a la compilation, donc un build de production repond `false`
 * quel que soit l'URL. Le parametre seul ne suffit jamais.
 */
export function isSyncDemoEnabled(search: string, isDev: boolean): boolean {
  if (!isDev) return false;
  const value = new URLSearchParams(search).get("syncDemo");
  // On accepte `?syncDemo=1` et `?syncDemo` (valeur vide), mais pas `=0`.
  return value !== null && value !== "0" && value !== "false";
}
